export type IntegrationCategory = 'project_management' | 'scheduling' | 'design_cad';
export type IntegrationAvailability = 'live' | 'beta' | 'planned';

export interface IntegrationQuickLink {
  label: string;
  route: string;
  description?: string;
}

export interface IntegrationSetupStep {
  title: string;
  body: string;
}

export interface ThirdPartyIntegration {
  id: string;
  name: string;
  vendor: string;
  category: IntegrationCategory;
  availability: IntegrationAvailability;
  description: string;
  features: string[];
  /** Route under /home */
  setupRoute: string;
  /** Optional doc in repo (relative path for display) */
  docHint?: string;
  accent: string;
  iconLabel: string;
  setupSteps?: IntegrationSetupStep[];
  quickLinks?: IntegrationQuickLink[];
}

export const INTEGRATION_CATEGORIES: { id: IntegrationCategory; label: string }[] = [
  { id: 'project_management', label: 'Project management' },
  { id: 'scheduling', label: 'Scheduling & WBS' },
  { id: 'design_cad', label: 'Design & CAD' },
];

export const THIRD_PARTY_INTEGRATIONS: readonly ThirdPartyIntegration[] = [
  {
    id: 'clickup',
    name: 'ClickUp',
    vendor: 'ClickUp',
    category: 'project_management',
    availability: 'live',
    description: 'Sync SigmaT WBS activities with ClickUp tasks — push, pull, and webhooks.',
    features: ['WBS ↔ tasks', 'Status & assignee sync', 'Webhook updates', 'Org/project token'],
    setupRoute: '/home/integrations/clickup',
    docHint: 'Docs/integrations/ClickUp-Integration.md',
    accent: '#7b68ee',
    iconLabel: 'CU',
  },
  {
    id: 'primavera',
    name: 'Primavera P6',
    vendor: 'Oracle',
    category: 'scheduling',
    availability: 'planned',
    description: 'Import XER/PMXML schedules into SigmaT WBS and track deviation against field progress.',
    features: ['XER import', 'Activity ↔ WBS', 'Baseline sync', 'Deviation reporting'],
    setupRoute: '/home/integrations/primavera',
    docHint: 'Docs/integrations/Enterprise-Integration-Roadmap.md',
    accent: '#c74634',
    iconLabel: 'P6',
    setupSteps: [
      {
        title: 'Prepare schedule export',
        body: 'Export the baseline from Primavera P6 as XER or PMXML. Ensure activity IDs are stable for mapping to SigmaT WBS nodes.',
      },
      {
        title: 'Use WBS as interim bridge',
        body: 'Until native XER import ships, maintain WBS in SigmaT via Excel import and compare against P6 using the deviation report.',
      },
      {
        title: 'Roadmap',
        body: 'Native P6 connector (Phase 1–2 of enterprise roadmap): automated import, UID mapping, and plan vs actual sync.',
      },
    ],
    quickLinks: [
      { label: 'WBS by project', route: '/home/work-breakdown-structure/list', description: 'Manage activities aligned to schedule' },
      { label: 'WBS deviation report', route: '/home/work-breakdown-structure/deviation-report', description: 'Compare planned vs executed' },
    ],
  },
  {
    id: 'microsoft-project',
    name: 'Microsoft Project',
    vendor: 'Microsoft',
    category: 'scheduling',
    availability: 'planned',
    description: 'Import MSP XML schedules and align tasks with SigmaT WBS and execution modules.',
    features: ['MSPDI XML', 'Task UID mapping', 'Plan vs actual', 'Excel bridge (interim)'],
    setupRoute: '/home/integrations/microsoft-project',
    docHint: 'Docs/integrations/Enterprise-Integration-Roadmap.md',
    accent: '#217346',
    iconLabel: 'MSP',
    setupSteps: [
      {
        title: 'Export from Microsoft Project',
        body: 'Save as XML (MSPDI) from Project desktop or Project Online. Keep task UIDs unchanged between exports.',
      },
      {
        title: 'Interim: Excel WBS import',
        body: 'Map MSP tasks to SigmaT WBS using the existing Excel template until MSPDI import is available.',
      },
      {
        title: 'Roadmap',
        body: 'Scheduled connector will import MSPDI, map task UIDs to WBS, and surface plan vs actual in deviation reporting.',
      },
    ],
    quickLinks: [
      { label: 'WBS by project', route: '/home/work-breakdown-structure/list', description: 'Import and edit WBS activities' },
      { label: 'WBS deviation report', route: '/home/work-breakdown-structure/deviation-report', description: 'Track schedule variance' },
    ],
  }
  // {
  //   id: 'cad',
  //   name: 'CAD & Drawings',
  //   vendor: 'AutoCAD / DXF',
  //   category: 'design_cad',
  //   availability: 'beta',
  //   description: 'View DWG/DXF drawings and import alignments into LRS/GIS for chainage-based asset management.',
  //   features: ['DWG/DXF viewer', 'Drawing uploads', 'DXF → LRS (roadmap)', 'GeoJSON export'],
  //   setupRoute: '/home/integrations/cad',
  //   docHint: 'Docs/integrations/Enterprise-Integration-Roadmap.md',
  //   accent: '#dc2626',
  //   iconLabel: 'CAD',
  //   setupSteps: [
  //     {
  //       title: 'Open CAD viewer',
  //       body: 'Upload and view DWG/DXF drawings in the built-in CAD viewer. Use layers and zoom for design review on project documents.',
  //     },
  //     {
  //       title: 'GIS & LRS alignment',
  //       body: 'For linear assets, use the map editor and LRS module to tie chainage to geometry. DXF → LRS import is on the roadmap.',
  //     },
  //     {
  //       title: 'Asset console',
  //       body: 'Link drawings to GIS assets once alignments are georeferenced in the asset management console.',
  //     },
  //   ],
  //   quickLinks: [
  //     { label: 'CAD viewer', route: '/home/cadviewer', description: 'View DWG/DXF drawings' },
  //     { label: 'Geospatial (GIS + LRS)', route: '/home/geospatial', description: 'Unified map editor with linear referencing' },
  //     { label: 'Asset console', route: '/home/asset-management', description: 'GIS asset management' },
  //   ],
  // },
] as const;

export function getIntegrationById(id: string): ThirdPartyIntegration | undefined {
  return THIRD_PARTY_INTEGRATIONS.find((i) => i.id === id);
}

export function availabilityLabel(a: IntegrationAvailability): string {
  switch (a) {
    case 'live':
      return 'Connected';
    case 'beta':
      return 'Beta';
    case 'planned':
      return 'Coming soon';
  }
}
